import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Camera, MapPin, Wallet, Wrench, FileText, CalendarClock, LogOut, ShieldCheck, Bell, Download } from "lucide-react";
import MessageThread from "@/components/site/MessageThread";

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: rep }] = await Promise.all([
          api.get("/owner/dashboard"),
          api.get("/owner/inspection-reports"),
        ]);
        setData(data);
        setReports(rep);
      } catch (e) { /* protected route handles */ }
    })();
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!data) return <div className="container-x py-16 text-brand-navy">Loading dashboard…</div>;

  const healthColor = data.health_score === "GREEN" ? "text-emerald-600 bg-emerald-50 border-emerald-200" :
    data.health_score === "AMBER" ? "text-amber-700 bg-amber-50 border-amber-200" :
    "text-red-700 bg-red-50 border-red-200";

  const r = data.rental_statement;

  return (
    <div data-testid="page-owner-dashboard" className="container-x py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="eyebrow">My Property Care Dashboard</div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold text-brand-navy">Welcome, {user?.name || data.owner_name}.</h1>
          <p className="mt-1 text-sm text-brand-slate/70">{data.property.title} · {data.property.address}</p>
        </div>
        <button data-testid="owner-logout" onClick={doLogout} className="btn-ghost-navy"><LogOut size={14} /> Logout</button>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="card-flat p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">Latest Inspection Report</div>
              <div className="mt-2 font-display text-2xl text-brand-navy font-bold">{data.latest_inspection.date} · {data.latest_inspection.inspector}</div>
              <p className="mt-2 text-sm text-brand-slate/80">{data.latest_inspection.summary}</p>
            </div>
            <div className={`text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-2 border ${healthColor}`}>Health · {data.health_score}</div>
          </div>
          <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
            <Info Icon={MapPin} label="Geo-tag" value={data.latest_inspection.geo} />
            <Info Icon={Camera} label="Photos" value={`${data.latest_inspection.photos} geo-tagged`} />
            <Info Icon={CalendarClock} label="Next Visit" value={`${data.next_visit.date} · ${data.next_visit.type}`} />
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="eyebrow">Rental Income Statement</div>
          <div className="mt-2 text-sm text-brand-slate/70">{r.month}</div>
          <div className="mt-4 space-y-2 text-sm">
            <Row label="Rent Received" value={`₹${r.rent_received.toLocaleString("en-IN")}`} positive />
            <Row label="Pending" value={`₹${r.pending.toLocaleString("en-IN")}`} />
            <Row label="Deductions" value={`-₹${r.deductions.toLocaleString("en-IN")}`} />
            <Row label="Maintenance Expenses" value={`-₹${r.maintenance_expenses.toLocaleString("en-IN")}`} />
          </div>
          <div className="mt-4 border-t border-brand-border pt-3 flex items-center justify-between">
            <span className="font-semibold text-brand-navy">Net Payout</span>
            <span className="font-display text-2xl font-bold text-brand-navy">₹{(r.rent_received - r.deductions - r.maintenance_expenses).toLocaleString("en-IN")}</span>
          </div>
        </div>

        <Card title="Open Maintenance Tickets" Icon={Wrench}>
          <div className="mt-3 font-display text-4xl font-bold text-brand-navy">{data.open_tickets}</div>
          <div className="text-xs text-brand-slate/60 mt-1">1 awaiting vendor approval · 1 quote pending</div>
        </Card>

        <Card title="Next Scheduled Visit" Icon={CalendarClock}>
          <div className="mt-3 font-display text-2xl font-bold text-brand-navy">{data.next_visit.date}</div>
          <div className="text-sm text-brand-slate/70 mt-1">{data.next_visit.type} · Ravi Kumar</div>
        </Card>

        <Card title="Reminders" Icon={Bell}>
          <ul className="mt-3 space-y-2 text-sm">
            {data.reminders.map((rm, i) => (
              <li key={i} className="flex items-center justify-between">
                <span>{rm.label}</span>
                <span className="text-xs uppercase tracking-[0.18em] text-brand-cyan font-semibold">{rm.date}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="mt-6 card-flat p-6" data-testid="owner-inspection-reports">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Inspection Reports</div>
            <div className="mt-2 font-display text-xl text-brand-navy font-bold">Field-verified reports uploaded by Hi-Tech.</div>
          </div>
          <FileText className="text-brand-cyan" />
        </div>
        {reports.length === 0 ? (
          <p className="mt-4 text-sm text-brand-slate/70">No reports uploaded yet. New reports will appear here automatically.</p>
        ) : (
          <ul className="mt-5 grid md:grid-cols-2 gap-3">
            {reports.map((r) => (
              <li key={r.id} className="border border-brand-border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-brand-navy text-sm">{r.title}</div>
                    <div className="text-xs text-brand-slate/70 mt-1">{r.date} · {r.inspector}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 border ${r.health_score === "GREEN" ? "text-emerald-600 bg-emerald-50 border-emerald-200" : r.health_score === "AMBER" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200"}`}>{r.health_score}</span>
                </div>
                <p className="text-xs text-brand-slate/70 mt-2 line-clamp-3">{r.summary}</p>
                {r.file_url && (
                  <a href={fileUrl(r.file_url)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-semibold text-brand-cyan hover:underline">
                    <Download size={12}/> Download PDF
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 card-flat p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Documents Vault</div>
            <div className="mt-2 font-display text-xl text-brand-navy font-bold">All your property documents — in one place.</div>
          </div>
          <ShieldCheck className="text-brand-cyan" />
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.documents.map((d) => (
            <div key={d.name} className="border border-brand-border p-4 flex items-center gap-3 hover:border-brand-cyan transition-colors">
              <FileText size={18} className="text-brand-cyan" />
              <div>
                <div className="text-sm font-semibold text-brand-navy">{d.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-slate/60">{d.type}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <MessageThread currentUserId={user?.id} />
      </div>
    </div>
  );
}

function Info({ Icon, label, value }) {
  return (
    <div className="border border-brand-border p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-brand-cyan">
        <Icon size={14} /> {label}
      </div>
      <div className="mt-2 text-sm font-semibold text-brand-navy">{value}</div>
    </div>
  );
}
function Row({ label, value, positive }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-brand-slate/70">{label}</span>
      <span className={`font-semibold ${positive ? "text-emerald-600" : "text-brand-navy"}`}>{value}</span>
    </div>
  );
}
function Card({ title, Icon, children }) {
  return (
    <div className="card-flat p-6">
      <div className="flex items-center justify-between">
        <div className="eyebrow">{title}</div>
        <Icon size={18} className="text-brand-cyan" />
      </div>
      {children}
    </div>
  );
}
